import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { ApiError } from '@/api/http';
import { queryKeyRoots } from '@/api/queryKeys';
import {
  restoreQuestionSet,
  retireQuestionSet,
  type QuestionSet,
  type QuestionSetDetail,
} from '@/api/questionSets';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { type } from '@/theme/typography';

interface Props {
  /** The row as it stands. Its `is_active` decides which of the two actions this dialog is. */
  set: QuestionSet;
  /** Called when the act has been **accepted**, before this closes — the table announces it. */
  onCompleted?: (set: QuestionSet, nowActive: boolean) => void;
  onClose: () => void;
}

/**
 * §6.9's **Retire a set** — and its **Restore**, which is the same shape with the other endpoint.
 *
 * ⚠️ **This is what "delete" means for a set, and it is feedback protection #2 one table over** (ADR
 * 0005, which ADR 0018 extends to `QuestionSet`). There is no delete: `POST …/retire/` sets
 * `is_active = False`, the row is never removed, and **every interview ever arranged from it keeps its
 * frozen questions** — `Interview.question_set` still points at it and every `Score` keeps its
 * `question_text_snapshot`. The API declares **no `DELETE` operation on any set path** for a client to
 * call, and `questionSets.test.tsx` asserts that no control on this tab matches `/delete|remove/i`
 * with the menu and each dialog open in turn.
 *
 * ⚠️ **What the confirmation says, and all three clauses are checked rather than paraphrased:**
 *
 *  1. **Interviews already arranged keep their questions.** The freeze happens when an interview is
 *     created (ADR 0004, feedback protection #1), so retiring takes effect at that boundary and
 *     nowhere else — a draft nobody has opened yet still asks exactly what it was arranged with.
 *  2. **The set stays in the workspace.** It is listed here still, reading *Retired*, and it is
 *     restorable from the same menu — which is also what makes the act legible as reversible.
 *  3. **It is not offered when an interview is scheduled.** `services._set_for` filters `is_active`,
 *     so the vacancy's fallback can never pick a retired set, and naming one explicitly is a **404**.
 *     That is the one thing retiring actually changes, so it is the one thing the reader is told it
 *     changes.
 *
 * ⚠️ **Deliberately light, and that is a decision.** No acknowledgement checkbox, no `danger` button,
 * no *"cannot be undone"* — retiring **can** be undone from the same menu, and ADR 0009's weight
 * belongs to §6.5's erasure, which has no undo anywhere. `ArchiveDialog` and `CloseVacancyDialog` took
 * the same decision for the same reason. What the dialog *is* for is that retiring is not a silent
 * toggle, and that a write fired straight from a menu item would have nowhere to report a 403, a
 * throttle or a lost connection.
 *
 * ⚠️ **Both requests carry no body at all** — `is_active` is on neither `QuestionSetIn` nor
 * `QuestionSetUpdate`, and the document declares **no `requestBody`** for either action, so a body
 * appearing here would be the first sign that retiring had become a field edit through the verb.
 * Asserted as `data === undefined` on both.
 *
 * ⚠️ **Both roots, and `roles` is not optional on these two.** `RoleOut.active_question_count` counts
 * the distinct active questions of a vacancy's **active** sets (`role_queryset`), so retiring a set an
 * attached vacancy offers takes its questions out of §6.8's cell — the client cannot tell from any
 * payload whether this set is attached anywhere, and asking the catalogue is cheaper than leaving it
 * wrong. That is the same argument `ArchiveDialog` makes about a question, one level up.
 */
export function RetireSetDialog({ set, onCompleted, onClose }: Props) {
  const { t } = useTranslation(['questions', 'common', 'components']);
  const queryClient = useQueryClient();

  /** Which direction this is. The row as it stands decides, and the menu offers only the other one. */
  const retiring = set.is_active;

  const write = useMutation<QuestionSetDetail, ApiError, void>({
    mutationFn: () => (retiring ? retireQuestionSet(set.id) : restoreQuestionSet(set.id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.questionSets });
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.roles });
      onCompleted?.(set, !retiring);
      onClose();
    },
    onError: (error) => {
      if (!error.isNotFound) return;
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.questionSets });
      void queryClient.invalidateQueries({ queryKey: queryKeyRoots.roles });
      onClose();
    },
  });

  /** §8 in a dialog: what failed, in words, never a status code and never the server's `detail`. */
  const failure = (() => {
    if (!write.error || write.error.isNotFound) return null;
    if (write.error.isOffline) return t('questions:sets.dialog.offline');
    if (write.error.isThrottled) return t('components:state.throttled');
    return t(retiring ? 'questions:sets.retire.failed' : 'questions:sets.restore.failed');
  })();

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t(retiring ? 'questions:sets.retire.title' : 'questions:sets.restore.title')}
      description={t(
        retiring ? 'questions:sets.retire.description' : 'questions:sets.restore.description',
      )}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common:cancel')}
          </Button>
          {/*
           * The ordinary primary button, not `danger`: §4 reserves the `negative` outline for what
           * cannot be taken back, and this is undone from the very same menu.
           */}
          <Button onClick={() => write.mutate()} isLoading={write.isPending}>
            {t(retiring ? 'questions:sets.retire.submit' : 'questions:sets.restore.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm text-ink-900">
        {/* Which row this is: the tab has one of these per set, and the name is what tells them apart. */}
        <div>
          <p className={type.eyebrow}>{t('questions:sets.table.columns.name')}</p>
          <p className="mt-0.5 [overflow-wrap:anywhere]">{set.name}</p>
        </div>

        <p className="font-medium">
          {t(retiring ? 'questions:sets.retire.scheduling' : 'questions:sets.restore.scheduling')}
        </p>
        <p>{t(retiring ? 'questions:sets.retire.arranged' : 'questions:sets.restore.arranged')}</p>
        <p className="text-ink-700">
          {t(retiring ? 'questions:sets.retire.place' : 'questions:sets.restore.place')}
        </p>

        {failure && (
          <p role="alert" className="text-negative">
            {failure}
          </p>
        )}
      </div>
    </Dialog>
  );
}
